<template>
  <div
    class="payroll-sidebar-shell"
    :class="{ 'payroll-sidebar-shell--open': isPanelOpen }"
    :style="{
      background: theme.background,
      color: theme.text,
      '--payroll-panel-width': `${panelWidth}px`,
      '--payroll-total-width': `${totalSidebarWidth}px`
    }"
  >
    <!-- ====================================================== -->
    <!-- LEVEL 1: TOP-LEVEL ICON RAIL -->
    <!-- ====================================================== -->

    <v-card
      flat
      class="payroll-sidebar-rail"
      :style="{
        background: theme.primary,
        color: theme.text
      }"
    >
      <v-list
        nav
        density="compact"
        class="payroll-sidebar-rail-list"
        :style="{
          background: theme.primary,
          color: theme.text
        }"
      >
        <template
          v-for="item in visibleRootMenu"
          :key="item.id"
        >
          <v-tooltip
            location="right"
            :text="item.title"
          >
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                class="payroll-sidebar-rail-item"
                :class="{
                  'payroll-sidebar-rail-item--active':
                    selectedRootId === item.id
                }"
                :active="selectedRootId === item.id"
                @click="selectRootItem(item)"
              >
                <template #default>
                  <div class="d-flex justify-center align-center w-100">
                    <v-icon
                      :icon="item.icon || 'mdi-circle-outline'"
                      size="24"
                    />
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-tooltip>
        </template>
      </v-list>
    </v-card>

    <!-- ====================================================== -->
    <!-- LEVEL 2: SLIDING CHILD MENU -->
    <!-- ====================================================== -->

    <v-slide-x-transition>
      <v-card
        v-if="isPanelOpen && selectedRoot"
        flat
        class="payroll-sidebar-panel"
        :style="{
          background: theme.background,
          color: theme.text,
          width: `${panelWidth}px`,
          minWidth: `${panelWidth}px`,
          maxWidth: `${panelWidth}px`
        }"
      >
        <!-- PANEL HEADER -->
        <div
          class="payroll-sidebar-panel-header"
          :style="{
            background: theme.primary,
            color: theme.text
          }"
        >
          <div class="d-flex align-center ga-2 min-width-0">
            <v-icon
              :icon="selectedRoot.icon || 'mdi-circle-outline'"
              size="22"
            />

            <div class="payroll-sidebar-panel-title">
              {{ selectedRoot.title }}
            </div>
          </div>

          <v-tooltip
            location="bottom"
            :text="
              currentLanguage === 'fr'
                ? 'Fermer le menu'
                : 'Close menu'
            "
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-chevron-left"
                variant="text"
                size="small"
                @click="closePanel"
              />
            </template>
          </v-tooltip>
        </div>

        <!-- SEARCH -->
        <div class="payroll-sidebar-search">
          <v-text-field
            v-model="search"
            :label="t('search')"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />
        </div>

        <v-divider />

        <!-- CHILD MENU -->
        <div class="payroll-sidebar-panel-content">
          <v-list
            v-model:opened="open"
            nav
            density="compact"
            class="payroll-sidebar-child-list"
            :style="{
              background: theme.background,
              color: theme.text
            }"
          >
            <template
              v-for="child in filteredSelectedChildren"
              :key="child.id"
            >
              <!-- CHILD GROUP -->
              <v-list-group
                v-if="child.children?.length"
                :value="child.id"
              >
                <template #activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    :prepend-icon="
                      child.icon ||
                      'mdi-folder-outline'
                    "
                    :title="child.title"
                    class="payroll-sidebar-group-item"
                    :class="{
                      'payroll-sidebar-group-item--active':
                        containsActiveRoute(child)
                    }"
                  />
                </template>

                <!-- GRANDCHILDREN -->
                <template
                  v-for="sub in child.children"
                  :key="sub.id"
                >
                  <!-- SUPPORT AN EXTRA NESTED LEVEL -->
                  <v-list-group
                    v-if="sub.children?.length"
                    :value="sub.id"
                  >
                    <template #activator="{ props }">
                      <v-list-item
                        v-bind="props"
                        :prepend-icon="
                          sub.icon ||
                          'mdi-folder-outline'
                        "
                        :title="sub.title"
                        class="payroll-sidebar-subgroup-item"
                        :class="{
                          'payroll-sidebar-group-item--active':
                            containsActiveRoute(sub)
                        }"
                      />
                    </template>

                    <v-list-item
                      v-for="leaf in sub.children"
                      :key="leaf.id"
                      :prepend-icon="
                        leaf.icon ||
                        'mdi-circle-small'
                      "
                      :title="leaf.title"
                      link
                      :to="leaf.to"
                      :class="{
                        'payroll-active-item':
                          isRouteActive(leaf.to)
                      }"
                    />
                  </v-list-group>

                  <!-- NORMAL GRANDCHILD -->
                  <v-list-item
                    v-else
                    :prepend-icon="
                      sub.icon ||
                      'mdi-circle-small'
                    "
                    :title="sub.title"
                    link
                    :to="sub.to"
                    :class="{
                      'payroll-active-item':
                        isRouteActive(sub.to)
                    }"
                  />
                </template>
              </v-list-group>

              <!-- NORMAL CHILD -->
              <v-list-item
                v-else
                :prepend-icon="
                  child.icon ||
                  'mdi-circle-small'
                "
                :title="child.title"
                link
                :to="child.to"
                :class="{
                  'payroll-active-item':
                    isRouteActive(child.to)
                }"
              />
            </template>

            <div
              v-if="filteredSelectedChildren.length === 0"
              class="payroll-sidebar-empty"
            >
              <v-icon
                icon="mdi-magnify-close"
                size="28"
                class="mb-2"
              />

              <div>
                {{
                  currentLanguage === 'fr'
                    ? 'Aucun menu trouvé'
                    : 'No menu items found'
                }}
              </div>
            </div>
          </v-list>
        </div>

        <!-- ================================================== -->
        <!-- RESIZE HANDLE -->
        <!-- ================================================== -->

        <div
          class="payroll-sidebar-resize-handle"
          :class="{
            'payroll-sidebar-resize-handle--active':
              isResizing
          }"
          @pointerdown="startResize"
        >
          <div class="payroll-sidebar-resize-grip" />
        </div>
      </v-card>
    </v-slide-x-transition>
  </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@/store/auth"
import { useI18n } from 'vue-i18n'

import { useLangStore } from '@/store/lang'
import { useThemeStore } from "@/store/auth"
import toast from 'vue-toastification'
const langStore = useLangStore()
const themeStore = useThemeStore()
const theme = computed(() => themeStore.themeStyles)
const lang = computed(() => langStore.currentLanguage)

const auth = useAuthStore()
const { t, locale } = useI18n()

const route = useRoute()
const router = useRouter()

const emit = defineEmits([
  'resize',
  'panel-state'
])

const isNbhostingAdmin = computed(() =>
  auth.loginName === 'nbhostingAdmin'
)
// SEARCH
const search = ref("")
const open = ref([])
const selectedRootId = ref(null)
const isPanelOpen = ref(false)

watch(
  () => langStore.currentLanguage,
  (newLang) => {
    locale.value = newLang
    },
    { immediate: true }
  )


// ============================================================================
// PAYROLL MENU
// ============================================================================

const payrollMenu = computed(() => [
  // ==========================================================================
  // DASHBOARD
  // ==========================================================================

  {
    id: "PayrollDashboard",
    title: "Dashboard",
    icon: "mdi-view-dashboard-outline",
    children: [
      { id: "payroll-dashboard-1", title: "Payroll Overview", icon: "mdi-view-dashboard", to: router.resolve({ name: "PayrollDashboard", params: { lang: lang.value } }).href },
    ]
  },

  // ==========================================================================
  // EMPLOYEE MANAGEMENT
  // ==========================================================================

  {
    id: "EmployeeManagement",
    title: "Employee Management",
    icon: "mdi-account-group-outline",
    children: [
      { id: "employee-1", title: "Employees", icon: "mdi-account-multiple", to: router.resolve({ name: "PayrollEmployees", params: { lang: lang.value } }).href },

      {
        id: "EmployeeOrganization",
        title: "Organization",
        icon: "mdi-office-building-outline",
        children: [
          { id: "employee-org-1", title: "Departments", icon: "mdi-office-building-outline", to: router.resolve({ name: "PayrollDepartments", params: { lang: lang.value } }).href },
          { id: "employee-org-2", title: "Business Units", icon: "mdi-domain", to: router.resolve({ name: "PayrollBusinessUnits", params: { lang: lang.value } }).href },
          { id: "employee-org-3", title: "Cost Centres", icon: "mdi-chart-donut", to: router.resolve({ name: "PayrollCostCentres", params: { lang: lang.value } }).href },
          { id: "employee-org-4", title: "Work Locations", icon: "mdi-map-marker-outline", to: router.resolve({ name: "PayrollWorkLocations", params: { lang: lang.value } }).href },
        ]
      },

      {
        id: "JobsPositions",
        title: "Jobs & Positions",
        icon: "mdi-briefcase-outline",
        children: [
          { id: "employee-job-1", title: "Positions", icon: "mdi-account-tie-outline", to: router.resolve({ name: "PayrollPositions", params: { lang: lang.value } }).href },
          { id: "employee-job-2", title: "Jobs", icon: "mdi-briefcase", to: router.resolve({ name: "PayrollJobs", params: { lang: lang.value } }).href },
          { id: "employee-job-3", title: "Job Categories", icon: "mdi-shape-outline", to: router.resolve({ name: "PayrollJobCategories", params: { lang: lang.value } }).href },
          { id: "employee-job-4", title: "Page Grade", icon: "mdi-shape-outline", to: router.resolve({ name: "PayrollPayGrade", params: { lang: lang.value } }).href },
        ]
      },

      { id: "employee-2", title: "Employment Contracts", icon: "mdi-file-sign", to: router.resolve({ name: "PayrollEmploymentContracts", params: { lang: lang.value } }).href },
      { id: "employee-3", title: "Bank Accounts", icon: "mdi-bank-outline", to: router.resolve({ name: "PayrollEmployeeBankAccounts", params: { lang: lang.value } }).href },
      { id: "employee-4", title: "Government Identifiers", icon: "mdi-card-account-details-outline", to: router.resolve({ name: "PayrollEmployeeGovernmentIdentifiers", params: { lang: lang.value } }).href },
    ]
  },

  // ==========================================================================
  // TIME & ATTENDANCE
  // ==========================================================================

  {
    id: "TimeAttendance",
    title: "Time & Attendance",
    icon: "mdi-clock-outline",
    children: [
      { id: "time-1", title: "Timesheets", icon: "mdi-calendar-clock-outline", to: router.resolve({ name: "PayrollEmployeeTimesheets", params: { lang: lang.value } }).href },
      { id: "time-2", title: "Timesheet Entries", icon: "mdi-clock-edit-outline", to: router.resolve({ name: "PayrollEmployeeTimesheetEntries", params: { lang: lang.value } }).href },

      {
        id: "LeaveTimeOff",
        title: "Leave & Time Off",
        icon: "mdi-calendar-account-outline",
        children: [
          { id: "leave-1", title: "Leave Requests", icon: "mdi-calendar-question", to: router.resolve({ name: "PayrollLeaveRequests", params: { lang: lang.value } }).href },
          { id: "leave-2", title: "Leave Types", icon: "mdi-format-list-bulleted-type", to: router.resolve({ name: "PayrollLeaveTypes", params: { lang: lang.value } }).href },
          { id: "leave-3", title: "Leave Balances", icon: "mdi-calendar-check-outline", to: router.resolve({ name: "PayrollEmployeeLeaveBalances", params: { lang: lang.value } }).href },
          { id: "leave-4", title: "Leave Balance Transactions", icon: "mdi-swap-horizontal", to: router.resolve({ name: "PayrollEmployeeLeaveBalanceTransactions", params: { lang: lang.value } }).href },
        ]
      },

      {
        id: "HolidayManagement",
        title: "Holiday Management",
        icon: "mdi-calendar-star",
        children: [
          { id: "holiday-1", title: "Holiday Calendars", icon: "mdi-calendar-month-outline", to: router.resolve({ name: "PayrollHolidayCalendars", params: { lang: lang.value } }).href },
          { id: "holiday-2", title: "Holidays", icon: "mdi-calendar-star", to: router.resolve({ name: "PayrollHolidays", params: { lang: lang.value } }).href },
        ]
      },
    ]
  },

  // ==========================================================================
  // PAYROLL PROCESSING
  // ==========================================================================

  {
    id: "Payroll",
    title: "Payroll",
    icon: "mdi-calculator-variant-outline",
    children: [
      { id: "payroll-1", title: "Pay Groups", icon: "mdi-account-multiple-check-outline", to: router.resolve({ name: "PayrollPayGroups", params: { lang: lang.value } }).href },
      { id: "payroll-2", title: "Pay Group Employees", icon: "mdi-account-group-outline", to: router.resolve({ name: "PayrollPayGroupEmployees", params: { lang: lang.value } }).href },
      { id: "payroll-3", title: "Payroll Periods", icon: "mdi-calendar-range-outline", to: router.resolve({ name: "PayrollPeriods", params: { lang: lang.value } }).href },
      { id: "payroll-4", title: "Payroll Runs", icon: "mdi-play-circle-outline", to: router.resolve({ name: "PayrollRuns", params: { lang: lang.value } }).href },
      { id: "payroll-5", title: "Run Workspace", icon: "mdi-view-dashboard-edit-outline", to: router.resolve({ name: "PayrollRunWorkspace", params: { lang: lang.value } }).href },

      {
        id: "PayrollResults",
        title: "Payroll Results",
        icon: "mdi-calculator-variant",
        children: [
          { id: "result-1", title: "Employee Payroll Results", icon: "mdi-account-cash-outline", to: router.resolve({ name: "PayrollEmployeePayrollResults", params: { lang: lang.value } }).href },
          { id: "result-2", title: "Payroll Result Lines", icon: "mdi-format-list-bulleted", to: router.resolve({ name: "PayrollEmployeePayrollResultLines", params: { lang: lang.value } }).href },
          { id: "result-3", title: "Payroll Balances", icon: "mdi-scale-balance", to: router.resolve({ name: "PayrollEmployeePayrollBalances", params: { lang: lang.value } }).href },
        ]
      },

      { id: "payroll-6", title: "Payroll Payments", icon: "mdi-cash-multiple", to: router.resolve({ name: "PayrollPayments", params: { lang: lang.value } }).href },
      { id: "payroll-7", title: "Payslips", icon: "mdi-file-document-outline", to: router.resolve({ name: "PayrollPayslips", params: { lang: lang.value } }).href },
    ]
  },

  // ==========================================================================
  // DEDUCTIONS & BENEFITS
  // ==========================================================================

  {
    id: "DeductionsBenefits",
    title: "Deductions & Benefits",
    icon: "mdi-puzzle-outline",
    children: [
      { id: "deduction-1", title: "Payroll Components Type", icon: "mdi-format-list-bulleted-square", to: router.resolve({ name: "PayrollComponentsTypes", params: { lang: lang.value } }).href },
      { id: "deduction-2", title: "Payroll Components", icon: "mdi-format-list-bulleted-square", to: router.resolve({ name: "PayrollComponents", params: { lang: lang.value } }).href },
      { id: "deduction-3", title: "Employee Components", icon: "mdi-account-details-outline", to: router.resolve({ name: "PayrollEmployeePayrollComponents", params: { lang: lang.value } }).href },
      { id: "deduction-4", title: "Deduction Arrangements", icon: "mdi-minus-circle-outline", to: router.resolve({ name: "PayrollEmployeeDeductionArrangements", params: { lang: lang.value } }).href },
      { id: "deduction-5", title: "Benefit Plans", icon: "mdi-heart-pulse", to: router.resolve({ name: "PayrollBenefitPlans", params: { lang: lang.value } }).href },
      { id: "deduction-6", title: "Benefit Enrollments", icon: "mdi-account-heart-outline", to: router.resolve({ name: "PayrollEmployeeBenefitEnrollments", params: { lang: lang.value } }).href },
    ]
  },

  // ==========================================================================
  // TAXES
  // ==========================================================================

  {
    id: "PayrollTaxes",
    title: "Taxes",
    icon: "mdi-bank-outline",
    children: [
      { id: "tax-1", title: "Employee Tax Profiles", icon: "mdi-account-cash-outline", to: router.resolve({ name: "PayrollEmployeeTaxProfiles", params: { lang: lang.value } }).href },
      { id: "tax-2", title: "Employer Tax Profiles", icon: "mdi-office-building-cog-outline", to: router.resolve({ name: "PayrollEmployerTaxProfiles", params: { lang: lang.value } }).href },
      { id: "tax-3", title: "Payroll Tax Tables", icon: "mdi-table-large", to: router.resolve({ name: "PayrollTaxTables", params: { lang: lang.value } }).href },
      { id: "tax-4", title: "Payroll Tax Brackets", icon: "mdi-format-list-numbered", to: router.resolve({ name: "PayrollTaxBrackets", params: { lang: lang.value } }).href },
      { id: "tax-5", title: "Payroll Rules", icon: "mdi-scale-balance", to: router.resolve({ name: "PayrollRules", params: { lang: lang.value } }).href },
      { id: "tax-6", title: "Jurisdiction Rules", icon: "mdi-earth", to: router.resolve({ name: "PayrollComponentJurisdictionRules", params: { lang: lang.value } }).href },
    ]
  },

  // ==========================================================================
  // REPORTS / YEAR END
  // ==========================================================================

  {
    id: "PayrollReports",
    title: "Reports",
    icon: "mdi-chart-box-outline",
    children: [
      { id: "report-1", title: "Payroll Reports", icon: "mdi-chart-bar", to: router.resolve({ name: "PayrollReports", params: { lang: lang.value } }).href },

      {
        id: "YearEndReporting",
        title: "Year-End Reporting",
        icon: "mdi-calendar-check-outline",
        children: [
          { id: "yearend-1", title: "Year-End Processing", icon: "mdi-calendar-check-outline", to: router.resolve({ name: "PayrollYearEnd", params: { lang: lang.value } }).href },
          { id: "yearend-2", title: "Report Definitions", icon: "mdi-file-cog-outline", to: router.resolve({ name: "PayrollYearEndReportDefinitions", params: { lang: lang.value } }).href },
          { id: "yearend-3", title: "Report Fields", icon: "mdi-form-textbox", to: router.resolve({ name: "PayrollYearEndReportFields", params: { lang: lang.value } }).href },
          { id: "yearend-4", title: "Field Mappings", icon: "mdi-transit-connection-variant", to: router.resolve({ name: "PayrollYearEndFieldMappings", params: { lang: lang.value } }).href },
          { id: "yearend-5", title: "Submissions", icon: "mdi-send-check-outline", to: router.resolve({ name: "PayrollYearEndSubmissions", params: { lang: lang.value } }).href },
          { id: "yearend-6", title: "Employee Statements", icon: "mdi-file-account-outline", to: router.resolve({ name: "PayrollEmployeeYearEndStatements", params: { lang: lang.value } }).href },
          { id: "yearend-7", title: "Statement Values", icon: "mdi-format-list-numbered", to: router.resolve({ name: "PayrollEmployeeYearEndStatementValues", params: { lang: lang.value } }).href },
        ]
      },
    ]
  },

  // ==========================================================================
  // SETTINGS / ACCOUNTING
  // ==========================================================================

  {
    id: "PayrollSettings",
    title: "Settings",
    icon: "mdi-cog-outline",
    children: [
      { id: "settings-1", title: "Legal Entities", icon: "mdi-domain", to: router.resolve({ name: "PayrollLegalEntities", params: { lang: lang.value } }).href },
      { id: "settings-2", title: "Payslip Templates", icon: "mdi-file-document-edit-outline", to: router.resolve({ name: "PayrollPayslipTemplates", params: { lang: lang.value } }).href },

      {
        id: "PayrollAccounting",
        title: "Accounting & General Ledger",
        icon: "mdi-book-open-variant",
        children: [
          { id: "accounting-1", title: "Accounting Profiles", icon: "mdi-book-cog-outline", to: router.resolve({ name: "PayrollAccountingProfiles", params: { lang: lang.value } }).href },
          { id: "accounting-3", title: "Payroll Journals", icon: "mdi-book-open-page-variant-outline", to: router.resolve({ name: "PayrollJournals", params: { lang: lang.value } }).href },
          { id: "accounting-4", title: "Payroll Journal Lines", icon: "mdi-format-list-bulleted", to: router.resolve({ name: "PayrollJournalLines", params: { lang: lang.value } }).href },
          { id: "accounting-5", title: "Posting Batches", icon: "mdi-bank-transfer", to: router.resolve({ name: "PayrollPostingBatches", params: { lang: lang.value } }).href },
        ]
      },

      { id: "settings-3", title: "CSV Import", icon: "mdi-file-delimited-outline", to: router.resolve({ name: "PayrollCsvImport", params: { lang: lang.value } }).href },
      { id: "settings-4", title: "Diagnostics", icon: "mdi-stethoscope", to: router.resolve({ name: "PayrollDiagnostics", params: { lang: lang.value } }).href },
      { id: "settings-5", title: "Audit Logs", icon: "mdi-history", to: router.resolve({ name: "PayrollAuditLogs", params: { lang: lang.value } }).href },
    ]
  },
])

//* -------------------------------------------------------
 //  LANGUAGE
//------------------------------------------------------- */
const currentLanguage = computed(() =>
  langStore.currentLanguage || 'en'
)



/* -------------------------------------------------------
   RESIZABLE SECOND PANE
------------------------------------------------------- */
const ICON_RAIL_WIDTH = 64
const DEFAULT_PANEL_WIDTH = 280
const MIN_PANEL_WIDTH = 220
const MAX_PANEL_WIDTH = 520

const panelWidth =
  ref(DEFAULT_PANEL_WIDTH)

const isResizing =
  ref(false)

const resizeStartX =
  ref(0)

const resizeStartWidth =
  ref(DEFAULT_PANEL_WIDTH)

const totalSidebarWidth =
  computed(() =>
    isPanelOpen.value
      ? ICON_RAIL_WIDTH + panelWidth.value
      : ICON_RAIL_WIDTH
  )

function emitSidebarWidth() {
  const detail = {
    railWidth:
      ICON_RAIL_WIDTH,

    panelWidth:
      isPanelOpen.value
        ? panelWidth.value
        : 0,

    totalWidth:
      totalSidebarWidth.value,

    isOpen:
      isPanelOpen.value
  }

  emit(
    'resize',
    detail
  )

  emit(
    'panel-state',
    detail
  )

  window.dispatchEvent(
    new CustomEvent(
      'nexgbits-sidebar-resize',
      {
        detail
      }
    )
  )
}

function getPointerX(event) {
  return Number(event?.clientX ?? 0)
}

let activeResizeTarget =
  null

function startResize(event) {
  if (!isPanelOpen.value) {
    return
  }

  if (
    event.pointerType === 'mouse' &&
    event.button !== 0
  ) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  isResizing.value =
    true

  resizeStartX.value =
    getPointerX(event)

  resizeStartWidth.value =
    panelWidth.value

  activeResizeTarget =
    event.currentTarget

  activeResizeTarget
    ?.setPointerCapture?.(
      event.pointerId
    )

  document.body.classList.add(
    'nexgbits-payroll-sidebar-resizing'
  )

  window.addEventListener(
    'pointermove',
    handleResize
  )

  window.addEventListener(
    'pointerup',
    stopResize
  )

  window.addEventListener(
    'pointercancel',
    stopResize
  )
}

function handleResize(event) {
  if (!isResizing.value) {
    return
  }

  event.preventDefault()

  const currentX =
    getPointerX(event)

  const delta =
    currentX -
    resizeStartX.value

  panelWidth.value =
    Math.min(
      MAX_PANEL_WIDTH,
      Math.max(
        MIN_PANEL_WIDTH,
        resizeStartWidth.value +
          delta
      )
    )

  emitSidebarWidth()
}

function stopResize(event) {
  if (!isResizing.value) {
    return
  }

  isResizing.value =
    false

  if (
    activeResizeTarget &&
    event?.pointerId != null
  ) {
    try {
      activeResizeTarget
        .releasePointerCapture?.(
          event.pointerId
        )
    } catch {
      // Ignore if capture was already released.
    }
  }

  activeResizeTarget =
    null

  document.body.classList.remove(
    'nexgbits-payroll-sidebar-resizing'
  )

  window.removeEventListener(
    'pointermove',
    handleResize
  )

  window.removeEventListener(
    'pointerup',
    stopResize
  )

  window.removeEventListener(
    'pointercancel',
    stopResize
  )

  emitSidebarWidth()
}

/* -------------------------------------------------------
   PERMISSION FILTERING
------------------------------------------------------- */
function canAccessItem(item) {
  if (!item) return false

  if (item.public === true) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(item.permission)
  ) {
    return false
  }

  return true
}

function filterByAccess(items) {
  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      if (item.children?.length) {
        const children =
          filterByAccess(item.children)

        if (children.length === 0) {
          return item.to
            ? { ...item, children: undefined }
            : null
        }

        return {
          ...item,
          children
        }
      }

      return item
    })
    .filter(Boolean)
}

/* -------------------------------------------------------
   ROOT MENU
------------------------------------------------------- */
const visibleRootMenu = computed(() =>
  filterByAccess(payrollMenu.value)
)

const selectedRoot = computed(() =>
  visibleRootMenu.value.find(
    item =>
      String(item.id) ===
      String(selectedRootId.value)
  ) || null
)

/* -------------------------------------------------------
   SEARCH CURRENT ROOT CHILDREN
------------------------------------------------------- */
function filterItems(items, query) {
  const q =
    String(query || '')
      .trim()
      .toLowerCase()

  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      const title =
        String(item.title || '')
          .toLowerCase()

      const match =
        !q ||
        title.includes(q)

      if (item.children?.length) {
        const filteredChildren =
          filterItems(
            item.children,
            q
          )

        if (
          filteredChildren.length > 0
        ) {
          return {
            ...item,
            children: filteredChildren
          }
        }

        return match
          ? {
              ...item,
              children:
                filterByAccess(
                  item.children
                )
            }
          : null
      }

      return match
        ? item
        : null
    })
    .filter(Boolean)
}

const filteredSelectedChildren =
  computed(() => {
    if (!selectedRoot.value) {
      return []
    }

    return filterItems(
      selectedRoot.value.children || [],
      search.value
    )
  })

/* -------------------------------------------------------
   ROUTE HELPERS
------------------------------------------------------- */
function normalizePath(value) {
  if (!value) {
    return ''
  }

  try {
    return new URL(
      value,
      window.location.origin
    ).pathname.replace(/\/+$/, '')
  } catch {
    return String(value)
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
  }
}

function isRouteActive(to) {
  if (!to) {
    return false
  }

  return (
    normalizePath(route.path) ===
    normalizePath(to)
  )
}

function containsActiveRoute(item) {
  if (!item) {
    return false
  }

  if (
    item.to &&
    isRouteActive(item.to)
  ) {
    return true
  }

  return (
    item.children || []
  ).some(containsActiveRoute)
}

function findRootForCurrentRoute() {
  return visibleRootMenu.value.find(
    root =>
      containsActiveRoute(root)
  ) || null
}

/* -------------------------------------------------------
   OPEN GROUP IDS
------------------------------------------------------- */
function collectGroupIds(
  items,
  results = []
) {
  ;(items || []).forEach(item => {
    if (item.children?.length) {
      results.push(item.id)

      collectGroupIds(
        item.children,
        results
      )
    }
  })

  return results
}

function refreshOpenGroups() {
  open.value =
    collectGroupIds(
      filteredSelectedChildren.value
    )
}

/* -------------------------------------------------------
   ROOT ICON CLICK
------------------------------------------------------- */
async function selectRootItem(item) {
  if (!item) {
    return
  }

  if (!item.children?.length) {
    if (item.to) {
      await router.push(item.to)
    }

    return
  }

  if (
    selectedRootId.value === item.id &&
    isPanelOpen.value
  ) {
    closePanel()
    return
  }

  selectedRootId.value =
    item.id

  search.value =
    ''

  isPanelOpen.value =
    true

  refreshOpenGroups()

  emitSidebarWidth()
}

function closePanel() {
  isPanelOpen.value =
    false

  search.value =
    ''

  open.value =
    []

  emitSidebarWidth()
}

/* -------------------------------------------------------
   KEEP ACTIVE ROUTE'S ROOT SELECTED
------------------------------------------------------- */
watch(
  () => route.path,
  () => {
    const root =
      findRootForCurrentRoute()

    if (root) {
      selectedRootId.value =
        root.id

      isPanelOpen.value =
        true
    } else if (
      !selectedRootId.value &&
      visibleRootMenu.value.length
    ) {
      selectedRootId.value =
        visibleRootMenu.value[0].id

      isPanelOpen.value =
        true
    }
  },
  {
    immediate: true
  }
)

/* -------------------------------------------------------
   SEARCH: EXPAND MATCHING GROUPS
------------------------------------------------------- */
watch(
  search,
  () => {
    refreshOpenGroups()
  }
)

/* -------------------------------------------------------
   MENU/LANGUAGE/PERMISSION CHANGES
------------------------------------------------------- */
watch(
  visibleRootMenu,
  roots => {
    if (
      roots.length &&
      !roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )
    ) {
      selectedRootId.value =
        roots[0].id
    }

    if (
      roots.length &&
      selectedRootId.value
    ) {
      isPanelOpen.value =
        true

      refreshOpenGroups()
    }
  },
  {
    immediate: true
  }
)

watch(
  visibleRootMenu,
  roots => {
    if (!roots.length) {
      selectedRootId.value =
        null

      isPanelOpen.value =
        false

      return
    }

    const selectedStillVisible =
      roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )

    if (!selectedStillVisible) {
      const routeRoot =
        findRootForCurrentRoute()

      selectedRootId.value =
        routeRoot?.id ||
        roots[0]?.id

      isPanelOpen.value =
        Boolean(
          selectedRootId.value
        )
    }
  }
)
/* -------------------------------------------------------
   RESIZE CLEANUP
------------------------------------------------------- */
onBeforeUnmount(() => {
  stopResize()

})

watch(
  totalSidebarWidth,
  () => {
    emitSidebarWidth()
  },
  {
    immediate: true
  }
)

</script>

<style scoped>
/* ========================================================================== */
/* SHELL */
/* ========================================================================== */

.payroll-sidebar-shell {
  display: flex;
  align-items: stretch;

  width: 64px;
  min-height: 100%;

  overflow: visible;

  transition:
    width 160ms ease;
}

.payroll-sidebar-shell--open {
  width:
    var(
      --payroll-total-width
    );
}

/* ========================================================================== */
/* ICON RAIL */
/* ========================================================================== */

.payroll-sidebar-rail {
  position: relative;

  display: flex;
  flex-direction: column;

  width: 64px;
  min-width: 64px;

  min-height: 100vh;

  border-radius: 0;

  z-index: 2;
}

/* ========================================================================== */
/* BRAND */
/* ========================================================================== */

.payroll-brand {
  min-height: 74px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 10px 6px;
}

.payroll-brand-icon {
  width: 38px;
  height: 38px;

  display: grid;
  place-items: center;

  border-radius: 50%;

  color: #ffffff;

  background:
    rgba(
      76,
      148,
      255,
      0.95
    );
}

.payroll-brand-text {
  display: none;
}

.payroll-brand-title {
  font-size: 14px;
  font-weight: 800;
}

.payroll-brand-subtitle {
  margin-top: 2px;

  font-size: 9px;
  font-weight: 700;

  opacity: 0.85;
}

.payroll-brand-divider {
  opacity: 0.22;
}

/* ========================================================================== */
/* RAIL LIST */
/* ========================================================================== */

.payroll-sidebar-rail-list {
  flex: 1 1 auto;

  padding: 8px 6px;
}

.payroll-sidebar-rail-item {
  width: 52px;

  min-height: 48px !important;

  margin: 4px 0;

  padding: 0 !important;

  border-radius:
    10px !important;

  justify-content:
    center;
}

.payroll-sidebar-rail-item
  :deep(
    .v-list-item__content
  ) {
  display: flex;

  justify-content:
    center;

  align-items:
    center;
}

.payroll-sidebar-rail-item--active {
  background:
    rgba(
      255,
      255,
      255,
      0.18
    ) !important;
}

.payroll-sidebar-rail-item:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.12
    ) !important;
}

/* ========================================================================== */
/* MODULE LABEL */
/* ========================================================================== */

.payroll-module-label {
  padding:
    12px 7px 18px;

  text-align:
    center;
}

.payroll-module-label-text {
  font-size:
    9px;

  font-weight:
    700;

  text-transform:
    uppercase;

  opacity:
    0.8;

  writing-mode:
    vertical-rl;

  transform:
    rotate(180deg);

  margin:
    0 auto;
}

/* ========================================================================== */
/* PANEL */
/* ========================================================================== */

.payroll-sidebar-panel {
  position: relative;

  flex:
    0 0 auto;

  width:
    var(
      --payroll-panel-width
    );

  min-width:
    var(
      --payroll-panel-width
    );

  max-width:
    var(
      --payroll-panel-width
    );

  min-height:
    100vh;

  border-radius:
    0;

  border-right:
    1px solid
    rgba(
      0,
      0,
      0,
      0.08
    );

  box-shadow:
    6px 0 18px
    rgba(
      0,
      0,
      0,
      0.08
    );

  z-index:
    1;
}

/* ========================================================================== */
/* PANEL HEADER */
/* ========================================================================== */

.payroll-sidebar-panel-header {
  min-height:
    56px;

  padding:
    8px 10px
    8px 14px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;
}

.payroll-sidebar-panel-title {
  min-width:
    0;

  max-width:
    185px;

  font-size:
    15px;

  font-weight:
    700;

  white-space:
    nowrap;

  overflow:
    hidden;

  text-overflow:
    ellipsis;
}

.min-width-0 {
  min-width:
    0;
}

/* ========================================================================== */
/* SEARCH */
/* ========================================================================== */

.payroll-sidebar-search {
  padding:
    12px;
}

/* ========================================================================== */
/* PANEL CONTENT */
/* ========================================================================== */

.payroll-sidebar-panel-content {
  max-height:
    calc(
      100vh - 130px
    );

  overflow-y:
    auto;

  overflow-x:
    hidden;
}

.payroll-sidebar-child-list {
  padding:
    6px;
}

.payroll-sidebar-group-item,
.payroll-sidebar-subgroup-item,
.payroll-sidebar-child-list
  :deep(
    .v-list-item
  ) {
  min-height:
    38px !important;

  border-radius:
    7px;

  margin-bottom:
    2px;
}

.payroll-sidebar-group-item {
  font-weight:
    600;
}

.payroll-sidebar-subgroup-item {
  font-weight:
    500;
}

.payroll-sidebar-group-item--active {
  background:
    rgba(
      25,
      118,
      210,
      0.1
    ) !important;
}

/* ========================================================================== */
/* ACTIVE ROUTE */
/* ========================================================================== */

.payroll-active-item {
  background:
    rgba(
      25,
      118,
      210,
      0.16
    ) !important;

  color:
    #1976d2 !important;

  font-weight:
    700;
}

.payroll-active-item
  :deep(
    .v-icon
  ) {
  color:
    #1976d2 !important;
}

/* ========================================================================== */
/* NESTING */
/* ========================================================================== */

.payroll-sidebar-child-list
  :deep(
    .v-list-group__items
    .v-list-item
  ) {
  padding-inline-start:
    24px !important;
}

.payroll-sidebar-child-list
  :deep(
    .v-list-group__items
    .v-list-group__items
    .v-list-item
  ) {
  padding-inline-start:
    38px !important;
}

/* ========================================================================== */
/* EMPTY STATE */
/* ========================================================================== */

.payroll-sidebar-empty {
  padding:
    32px 12px;

  text-align:
    center;

  opacity:
    0.7;

  font-size:
    14px;
}

/* ========================================================================== */
/* RESIZE HANDLE */
/* ========================================================================== */

.payroll-sidebar-resize-handle {
  position:
    absolute;

  top:
    0;

  right:
    0;

  width:
    12px;

  height:
    100%;

  cursor:
    ew-resize;

  z-index:
    20;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  user-select:
    none;

  touch-action:
    none;
}

.payroll-sidebar-resize-handle::before {
  content:
    '';

  position:
    absolute;

  top:
    0;

  bottom:
    0;

  left:
    3px;

  width:
    2px;

  background:
    rgba(
      0,
      0,
      0,
      0.08
    );

  transition:
    background
      120ms ease,
    width
      120ms ease;
}

.payroll-sidebar-resize-handle:hover::before,
.payroll-sidebar-resize-handle--active::before {
  width:
    3px;

  background:
    rgba(
      25,
      118,
      210,
      0.55
    );
}

.payroll-sidebar-resize-grip {
  width:
    3px;

  height:
    42px;

  border-radius:
    999px;

  background:
    rgba(
      0,
      0,
      0,
      0.18
    );

  opacity:
    0;

  transition:
    opacity
    120ms ease;
}

.payroll-sidebar-resize-handle:hover
  .payroll-sidebar-resize-grip,
.payroll-sidebar-resize-handle--active
  .payroll-sidebar-resize-grip {
  opacity:
    1;
}

/* ========================================================================== */
/* GLOBAL RESIZE STATE */
/* ========================================================================== */

:global(
  body.nexgbits-payroll-sidebar-resizing
) {
  cursor:
    ew-resize !important;

  user-select:
    none !important;
}

:global(
  body.nexgbits-payroll-sidebar-resizing *
) {
  cursor:
    ew-resize !important;
}

:global(
  body.nexgbits-payroll-sidebar-resizing
)
.payroll-sidebar-shell {
  transition:
    none !important;
}

/* ========================================================================== */
/* SMALL VIEWPORTS */
/* ========================================================================== */

@media (
  max-width: 600px
) {
  .payroll-sidebar-shell--open {
    width:
      min(
        var(
          --payroll-total-width
        ),
        100vw
      );
  }

  .payroll-sidebar-panel {
    width:
      min(
        var(
          --payroll-panel-width
        ),
        calc(
          100vw - 64px
        )
      );

    min-width:
      min(
        var(
          --payroll-panel-width
        ),
        calc(
          100vw - 64px
        )
      );

    max-width:
      calc(
        100vw - 64px
      );
  }

  .payroll-sidebar-resize-handle {
    display:
      none;
  }
}
</style>